/**
 * What DATABASE is behind an access-list target — the fact the band, the mapping
 * chips and the drawer all needed and none of them had.
 *
 * A target row says `atlas` or `do_db` (052). `do_db` is a KIND, not an engine: the
 * same kind covers a Postgres, a Valkey and a MySQL firewall, which read identically
 * on screen today. The engine lives on the scanned `do_databases` row whose `uuid`
 * equals the target's `projectId`, so the join is client-side and needs no new field
 * (spec OQ-2 default: no backend change).
 *
 * Pure and dependency-free, same rule as `targetKind.ts`: the mapping is one table,
 * and every surface reads it rather than testing `engine === 'pg'` inline.
 */
import { asTargetKind } from './targetKind';

/** The five things a target can BE on screen. `unknown` is a real answer, not a gap:
 *  a do_db target added before the first scan has no `do_databases` row at all. */
export type TargetEngine = 'atlas' | 'pg' | 'valkey' | 'mysql' | 'unknown';

/** Accessible name + hover title. Never an abbreviation — the icon is the short form. */
export const ENGINE_LABEL: Record<TargetEngine, string> = {
  atlas: 'MongoDB Atlas',
  pg: 'PostgreSQL',
  // Redis and Valkey share a glyph and a name: DO reports either string for the same
  // product line, and two glyphs would imply a distinction the firewall does not make.
  valkey: 'Valkey / Redis',
  mysql: 'MySQL',
  unknown: 'unknown engine',
};

/**
 * A `do_databases.engine` string → our five. The column is FREE TEXT from the DO
 * payload (migration 052:98), so anything unrecognised must land on `unknown` rather
 * than throw or render blank.
 */
export function engineFromDoEngine(engine: string | null | undefined): TargetEngine {
  const e = (engine ?? '').trim().toLowerCase();
  if (e === 'pg' || e === 'postgres' || e === 'postgresql') return 'pg';
  if (e === 'valkey' || e === 'redis') return 'valkey';
  if (e === 'mysql') return 'mysql';
  return 'unknown';
}

/** The scanned row this join needs — a structural subset of `DoDatabaseOption`. */
export interface EngineSource {
  uuid: string;
  engine?: string | null;
}

/**
 * The engine of one target.
 *
 * Atlas is decided by the KIND alone — there is no scanned row to look up and never
 * will be. `do_db` is decided by the join, and a miss is `unknown` (AC-5): a stale or
 * never-run scan must degrade to a generic glyph, not to a wrong one.
 */
export function engineForTarget(
  target: { kind?: string | null; projectId?: string | null } | null | undefined,
  byUuid?: Map<string, EngineSource> | null,
): TargetEngine {
  if (!target) return 'unknown';
  if (asTargetKind(target.kind) === 'atlas') return 'atlas';
  const uuid = target.projectId ?? '';
  const row = uuid && byUuid ? byUuid.get(uuid) : undefined;
  return row ? engineFromDoEngine(row.engine) : 'unknown';
}

/** `uuid → row`, built once per render of a screen that lists many targets. */
export function engineIndex(rows: readonly EngineSource[] | null | undefined): Map<string, EngineSource> {
  const m = new Map<string, EngineSource>();
  for (const r of rows ?? []) if (r?.uuid) m.set(r.uuid, r);
  return m;
}
